import { isDeepStrictEqual } from 'node:util';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FC } from 'react';
import { Box, Spinner, Text } from '@wix/cli-ui-kit';
import { useHttpClient } from '@wix/cli-http-client-react';
import { looseAppManifestSchema } from '@wix/cli-app-manifest';
import type { LooseAppManifest } from '@wix/cli-app-manifest';
import { useARMClient } from '@wix/arm-client';
import { useTunnel } from '@wix/cli-tunnel';
import { readUserConfig } from '@wix/cli-user-config';
import { isType, variant, fields } from 'variant';
import { useProjectModel } from '../../model.js';
import { createAstroLogger } from './utils/createAstroLogger.js';
import { PlatformLink } from '../PlatformLink/PlatformLink.js';
import { useUpdateManifest } from '../PreviewCommand/hooks/useUpdateManifest.js';
import { readAppConfig, saveAppConfig } from '../../files/app.config.js';
import { useUpdateDeploymentTopology } from '../../hooks/useUpdateDeploymentTopology.js';
import { isTunnelRequired } from './utils/isTunnelRequired.js';
import { TunnelingConsent } from './TunnelingConsent.js';

const DevCenterUpdateResult = variant({
  Changed: fields<{ armTagChanged: boolean }>(),
  Unchanged: {},
});

export const DevCenterUpdater: FC<{
  serverLocalUrl: string;
  serverExposedUrl?: string;
}> = ({ serverLocalUrl, serverExposedUrl }) => {
  const [devArmTag, setDevArmTag] = useState('');
  const [manifest, setManifest] = useState<LooseAppManifest | null>(null);

  const {
    model: {
      config: { siteId, appId },
      projectFolder,
    },
  } = useProjectModel();

  const logger = useMemo(() => createAstroLogger(), []);
  const http = useHttpClient({ type: 'standalone' });
  const { updateManifest } = useUpdateManifest();
  const arm = useARMClient();
  const updateDeploymentTopology = useUpdateDeploymentTopology();

  const openTunnel = useTunnel();
  const tunnelUrl = useRef<string>();
  const [tunnelRequired, setTunnelRequired] = useState(false);

  const updateDevCenter = useCallback(async () => {
    // TODO: read url from build manifest ?
    const url = new URL('/_wix/app-manifest.json', serverLocalUrl).href;
    const { data: responseFromDevServer } = await http.get(url);
    const latestManifest = looseAppManifestSchema.parse(responseFromDevServer);

    if (isDeepStrictEqual(manifest, latestManifest)) {
      return DevCenterUpdateResult.Unchanged();
    }

    if (
      !serverExposedUrl &&
      !tunnelUrl.current &&
      isTunnelRequired(latestManifest)
    ) {
      const config = await readUserConfig();
      if (!config.tunneling) {
        setTunnelRequired(true);
        return DevCenterUpdateResult.Unchanged();
      }

      const serverLocalPort = parseInt(new URL(serverLocalUrl).port, 10);
      const tunnel = await openTunnel(serverLocalPort);
      tunnelUrl.current = tunnel.url;
    }

    const baseUrl = serverExposedUrl ?? serverLocalUrl;
    const appConfig = await readAppConfig(projectFolder);

    let updatedDevArmTag = appConfig?.devArmTag;
    if (!updatedDevArmTag) {
      updatedDevArmTag = await arm.createLooseAppRelease(
        updateManifest(latestManifest, baseUrl, tunnelUrl.current),
        appId
      );
    } else {
      const updateState = await arm.updateLooseAppRelease(
        updateManifest(latestManifest, baseUrl, tunnelUrl.current),
        updatedDevArmTag
      );
      if (!isType(updateState, 'TagUpdated')) {
        updatedDevArmTag = await arm.createLooseAppRelease(
          updateManifest(latestManifest, baseUrl, tunnelUrl.current),
          appId
        );
      }
    }

    if (updatedDevArmTag !== appConfig?.devArmTag) {
      await saveAppConfig(projectFolder, { devArmTag: updatedDevArmTag });
    }

    setManifest(latestManifest);
    setDevArmTag(updatedDevArmTag);

    await updateDeploymentTopology.execute({
      serverUrl: baseUrl,
      armTag: updatedDevArmTag,
      environment: 'development',
    });

    const armTagChanged =
      !!appConfig?.devArmTag && updatedDevArmTag !== appConfig.devArmTag;
    return DevCenterUpdateResult.Changed({ armTagChanged });
  }, [
    http,
    updateManifest,
    serverLocalUrl,
    serverExposedUrl,
    arm,
    manifest,
    projectFolder,
    appId,
    updateDeploymentTopology,
    openTunnel,
  ]);

  const [timer, setTimer] = useState(Date.now());

  useEffect(() => {
    let disposed = false;

    void updateDevCenter()
      .then((result) => {
        if (isType(result, 'Changed')) {
          logger.log(
            'App Manifest updated. Refresh existing browser tabs to view the latest changes.'
          );
        }
      })
      .catch((error: unknown) => {
        // TODO: need product decision on how to format/color the output
        logger.log('Failed to update App Manifest');
        logger.log(`${error}`);
      })
      .then(() => {
        if (!disposed) {
          setTimeout(() => setTimer(Date.now()), 1_000);
        }
      });
    return () => {
      disposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  return (
    <Box rowGap={1} marginY={1} flexDirection="column">
      <TunnelingConsent required={tunnelRequired}>
        <Text>Open the preview on:</Text>
        <Box paddingLeft={2} flexDirection="column">
          <PlatformLink url={serverLocalUrl} label={'Site'} />
          {devArmTag ? (
            <PlatformLink
              url={`https://manage.wix.com/dashboard/${siteId}?apps-override=${devArmTag}`}
              label={'Dashboard'}
            />
          ) : (
            <Spinner text={'Preparing your dashboard...'} />
          )}
        </Box>
      </TunnelingConsent>
    </Box>
  );
};
